import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import PromoCard from '../ServiceView/component/PromoCard';
import usePromotion from '../../stores/usePromo';
import { Button } from '../../components/ui/button';
import Skeleton from '../../components/ui/skeleton';
import Heading from '../../components/Heading';
import { PROMO_PAGE } from '../../routes/routeConstant';
import { LanguageContext } from '../../contexts/LanguageContext';

const PROMO_CARD_GAP = 8;
const calculateInitialSlidesPerView = () => {
  const width = window.innerWidth;
  if (width < 300) return 1.6;
  if (width < 400) return 2;
  if (width < 620) return 2.8;
  if (width < 700) return 3;
  if (width < 980) return 3.5;
  if (width > 980) return 4.8;
  return 4;
};

export default function PromoSlide() {
  const { promotions, hasFetched, fetchLoading, fetchPromotions } = usePromotion();
  const [slidesPerView, setSlidesPerView] = useState(calculateInitialSlidesPerView);
  const [cardWidth, setCardWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const [visiblePromo, setVisiblePromo] = useState(promotions.slice(0, 6));

  useEffect(() => {
    if (!hasFetched && !fetchLoading) {
      fetchPromotions(1, true);
    }
  }, [fetchPromotions, fetchLoading, hasFetched]);

  useEffect(() => {
    if (promotions.length > 0) {
      setVisiblePromo(promotions.slice(0, 6));
    }
  }, [promotions]);

  const updateLayout = useCallback(() => {
    const width = window.innerWidth;

    let newCardWidth = 250;
    if (width < 370) newCardWidth = 158;
    else if (width < 640) newCardWidth = 150;
    else if (width < 1024) newCardWidth = 180;

    setCardWidth(newCardWidth);

    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const calculatedSlides = containerWidth / (newCardWidth + PROMO_CARD_GAP);
      let slidesToSet;

      if (width < 300) {
        slidesToSet = 1.6;
      } else if (width < 400) {
        slidesToSet = 2;
      } else if (width < 620) {
        slidesToSet = 2.5;
      } else if (width < 700) {
        slidesToSet = 2.5;
      } else if (width < 900) {
        slidesToSet = 3.2;
      } else {
        slidesToSet = Math.floor(calculatedSlides);
      }

      setSlidesPerView(slidesToSet);
    }
  }, []);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [updateLayout]);

  return (
    <section
      ref={containerRef}
      className={
        promotions.length === 0
          ? 'hidden'
          : 'relative rounded-lg gap-3 bg-neutral-100 flex flex-col items-center py-8 overflow-hidden px-8 md:w-full w-[96.5vw]'
      }
    >
      <Heading>{t('navigation.deals')}</Heading>

      <p className="text-secondNeutral text-xs sm:text-sm max-w-[540px] text-center">{t('tagline.home.promo')}</p>

      <Link to={PROMO_PAGE}>
        <Button variant="link" className="text-secondary">
          {t('footer.services.see_more')}
          <ArrowRight />
        </Button>
      </Link>

      <div className="relative">
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 3,
          }}
          loop
          slidesPerView={slidesPerView}
          spaceBetween={PROMO_CARD_GAP}
          className="relative w-[90vw] sm:max-w-screen-lg"
        >
          {fetchLoading ? (
            <>
              {Array.from({ length: 10 }).map(() => (
                <SwiperSlide key={Math.random()} style={{ width: `${cardWidth}px` }}>
                  <Skeleton className={`w-[${cardWidth}px] shadow-md sm:h-64 h-48`} />
                </SwiperSlide>
              ))}
            </>
          ) : (
            <>
              {visiblePromo.map((promotion) => (
                <SwiperSlide key={promotion.promo_id} className="pb-12" style={{ width: `${cardWidth}px` }}>
                  <PromoCard
                    service={
                      lang
                        ? promotion.service.name_srv_id
                        : promotion.service.name_srv_en || promotion.service.name_srv_id
                    }
                    name={lang ? promotion.name_prm : promotion.name_prm_en || promotion.name_prm}
                    price={promotion.price_prm}
                    imageUrl={promotion.image_prm || '/assets/no-data-image.svg'}
                    className="w-full"
                  />
                </SwiperSlide>
              ))}
            </>
          )}
        </Swiper>
      </div>
    </section>
  );
}
